<template>
  <div
    class="InsuredInfoComponent"
    :key="forWhom + forWhomPeople"
  >
    <FormField>
      <PostalCodeComponent
        :id="`postal-code-0`"
        v-model="policyHolderPostal"
        :label="postalCodeStatement(insureds[0].role, 0)"
        :placeholder="t('rater.common.more-info.postal-code.placeholder')"
        :rules="[...cpValidationRules, validationRules.required]"
        @serviceLoading="isServiceLoading[0] = $event"
      />
    </FormField>

    <BaseCheckBox
      v-if="!onlyHolderPostalCode && forWhom !== ForWhomEnum.ME && insureds.length > 1"
      id="forwhom"
      class="mb-3"
      :label="t('rater.common.more-info.postal-code.same-postal-code')"
      v-model="samePostalCode"
      :value="samePostalCode"
    />

    <template v-if="!onlyHolderPostalCode && !samePostalCode">
      <template
        v-for="(insured, index) in insureds"
        :key="index"
      >
        <div v-if="index > 0">
          <FormField>
            <PostalCodeComponent
              :id="`postal-code-${index}`"
              v-model="insured.addressDetails.postalCode"
              :label="postalCodeStatement(insured.role, index)"
              :placeholder="t('rater.common.more-info.postal-code.placeholder')"
              :rules="[...cpValidationRules, validationRules.required]"
              @serviceLoading="isServiceLoading[index] = $event"
            />
          </FormField>
        </div>
      </template>
    </template>

    <h3 v-if="showTitle">
      {{ birthDateTitle }}
    </h3>

    <FormField
      v-for="(insured, index) in insureds"
      :key="index"
      :statement="birthDateStatement(insured, index)"
    >
      <BirthDatePickerComponent
        :key="`birth-${index}-${refresh}-${insured.userDetails.birthDate || 'empty'}`"
        :legend="t('rater.common.more-info.birth-date.legend')"
        :id="`birthDate-${index}`"
        v-model="insured.userDetails.birthDate"
        :rules="rulesToApply(insured.role)"
      />
    </FormField>
  </div>
</template>
<script setup lang="ts">
  import type { Ref } from 'vue'
  import { computed, ref, watch } from 'vue'

  import { ForWhomEnum, UserRoleEnum } from '@/modules/core/models/enums'
  import type { FormAnswersType, ValidationRuleType } from '@/modules/core/models/types'

  import Insured from '@/modules/common/common/models/APP/Insured'
  import AddressDetails from '@/modules/common/hiring/models/APP/AddressDetails'
  import UserDetails from '@/modules/common/hiring/models/APP/UserDetails'
  import BirthDatePickerComponent from '@/modules/core/components/formFields/BirthDatePickerComponent.vue'
  import PostalCodeComponent from '@/modules/core/components/formFields/PostalCodeComponent.vue'
  import BaseCheckBox from '@/modules/core/components/ui/BaseCheckBox.vue'
  import FormField from '@/modules/core/components/utils/FormField.vue'
  import { getRoleMinMaxAge } from '@/modules/core/composables/minMaxAgeComposable'
  import { useRules } from '@/modules/core/composables/useRulesComposable'
  import { getBranchMaxAge } from '@/modules/core/utils/BranchUtils'
  import DentalRelationshipType from '@/modules/dental/common/models/DXP/DentalRelationshipType'
  import { arrayRange, repeat } from '@/utils/array.utils'
  import { useI18n } from 'vue-i18n'
  import type Configurations from '../../models/APP/Configuration'
  //TODO: Check if this component could be better

  const emit = defineEmits(['submit', 'change'])
  defineExpose({ submit })
  const { t } = useI18n()
  const { required, validateDate, tooOld, tooYoung, notFutureDate, tooOldGreaterOrEqual } =
    useRules()

  interface PropsInterface {
    showTitle?: boolean
    answers: FormAnswersType
    relationships?: DentalRelationshipType[]
    onlyHolderPostalCode?: boolean
    cpValidationRules?: ValidationRuleType[]
    useTooOldGreaterOrEqual?: boolean
    configurations?: Configurations
  }
  const props = withDefaults(defineProps<PropsInterface>(), {
    showTitle: true,
    cpValidationRules: () => [],
    answers: () => ({})
  })

  const startDate = props.answers.startDate

  const validationRules = {
    required,
    validateDate,
    tooOld: (maxAge: number) => (value: string) =>
      tooOld(value, maxAge, startDate, `common.validations.max-age-${getBranchMaxAge()}`),
    tooOldGreaterOrEqual: (maxAge: number) => (value: string) =>
      tooOldGreaterOrEqual(value, maxAge, `common.validations.max-age-${getBranchMaxAge()}`),
    tooYoung: (minAge: number) => (value: string) => tooYoung(value, minAge, new Date()),
    notFutureDate,
    startDateRequired: () => required(startDate, 'common.validations.start-date')
  }

  function rulesToApply(role: string): ValidationRuleType[] {
    const ages = getRoleMinMaxAge(props.configurations!, role)

    if (role !== UserRoleEnum.INSURED)
      return [
        validationRules.required,
        validationRules.validateDate,
        validationRules[
          props.useTooOldGreaterOrEqual
            ? role === UserRoleEnum.HOLDER_NOT_INSURED
              ? 'tooOld'
              : 'tooOldGreaterOrEqual'
            : 'tooOld'
        ](Number(ages.max)),
        validationRules.tooYoung(Number(ages.min)),
        validationRules.startDateRequired
      ]

    return [
      validationRules.required,
      validationRules.validateDate,
      validationRules[props.useTooOldGreaterOrEqual ? 'tooOldGreaterOrEqual' : 'tooOld'](
        Number(ages.max)
      ),
      validationRules.notFutureDate
    ]
  }

  const forWhom = computed(() => props.answers.forWhom)
  const forWhomPeople = computed(() => props.answers.forWhomPeople)
  const insureds: Ref<Insured[]> = ref([])
  insureds.value = getInsuredValues()
  const samePostalCode = ref<boolean>(areSamePostalCode(insureds.value))
  const isServiceLoading = ref<boolean[]>(repeat(forWhomPeople.value, false))
  const policyHolderPostal = ref(
    props.answers.policyHolderPostal || insureds.value[0].addressDetails.postalCode
  )

  const birthDateTitle = computed(() => {
    return t(`rater.common.more-info.birth-date.${getPrefixValue()}`)
  })

  watch(forWhom, () => (insureds.value = getDefaultInsuredInfo()))
  watch(forWhomPeople, () => (insureds.value = getInsuredValues()))
  watch(insureds, () => emit('change'), { deep: true })
  watch(policyHolderPostal, () => emit('change'))
  const refresh = ref(0)
  watch(
    () => props.configurations && props.configurations.hiringMaxAges,
    () => {
      refresh.value++
    },
    { deep: true, immediate: true }
  )
  watch(
    () => props.answers.insureds,
    (newInsureds) => {
      if (newInsureds && newInsureds.length > 0) {
        insureds.value = getInsuredValues()
        refresh.value++
      }
    },
    { deep: true, immediate: true }
  )

  function areSamePostalCode(insured: Insured[]) {
    const firstPostalCode = getFirstPostalCode()
    return insured.every((i) => i.addressDetails.postalCode === firstPostalCode)
  }

  function getFirstPostalCode() {
    const firstInsured = insureds.value[0]

    return firstInsured?.addressDetails?.postalCode
  }

  function fillPostalCodes(postalCode: string | null | undefined) {
    insureds.value.forEach((i) => (i.addressDetails.postalCode = postalCode))
  }

  function isHolderInsured(role: UserRoleEnum) {
    return role === UserRoleEnum.HOLDER_INSURED
  }

  function postalCodeStatement(role: string, index: number) {
    if (props.answers.forWhom === ForWhomEnum.ME) {
      return t(`rater.common.more-info.postal-code.span-list.me`)
    }

    if (role !== UserRoleEnum.INSURED) {
      const insuredCount = props.answers.forWhomPeople > 1 ? index + 1 : null
      return t(`rater.common.more-info.postal-code.span-list.holder`, { index: insuredCount })
    }
    if (props.onlyHolderPostalCode) {
      return t(`rater.common.more-info.postal-code.span-list.only-holder`)
    }
    const insuredCount = props.answers.forWhomPeople > 0 ? index + 1 : null
    return t(`rater.common.more-info.postal-code.span-list.not-holder`, { index: insuredCount })
  }

  function birthDateStatement(insured: Insured, index: number) {
    if (props.answers.forWhom === ForWhomEnum.ME) {
      return t(`rater.common.more-info.birth-date.span-list.me`)
    }

    const i18nTag = insured.isHolder
      ? 'holder'
      : props.showTitle
      ? 'not-holder'
      : 'not-holder-no-title'
    const insuredCount = props.answers.forWhomPeople > 1 ? index + 1 : null

    let text = t(`rater.common.more-info.birth-date.span-list.${i18nTag}`, { index: insuredCount })
    if (!insured.isHolder && props.relationships) {
      const relationship = props.relationships.find((r) => r.id === insured.relationship)!
      if (relationship?.text) text += ` (${relationship.text})`
    }
    return text
  }

  function getDefaultInsuredInfo() {
    const length = props.answers.forWhomPeople

    return arrayRange(1, length).map((i) => {
      const isFirst = i === 1

      const isHolder =
        isFirst &&
        isHolderInsured(
          // from rater birthdate info ?? from modify modal
          props.answers.holder?.role ?? props.answers.insureds?.[i - 1]?.role
        )
      const role = isHolder ? UserRoleEnum.HOLDER_INSURED : UserRoleEnum.INSURED

      const insured = props.answers.insureds[i - 1]
      const birthDate = insured?.userDetails.birthDate
      const postalCode = insured?.addressDetails.postalCode

      return new Insured({
        role,
        userDetails: new UserDetails({ birthDate }),
        addressDetails: new AddressDetails({ postalCode })
      })
    })
  }

  function getInsuredValues() {
    const hasPreviousData = !!props.answers.insureds.length
    if (hasPreviousData) {
      return props.answers.insureds.map(
        (insured: Insured) =>
          new Insured({
            role: insured.role,
            userDetails: new UserDetails({ birthDate: insured.userDetails.birthDate }),
            addressDetails: new AddressDetails({ postalCode: insured.addressDetails.postalCode }),
            relationship: insured.relationship,
            isHolder: insured.isHolder
          })
      )
    }
    return getDefaultInsuredInfo()
  }

  function getPrefixValue() {
    if (props.answers.forWhom === ForWhomEnum.ME) {
      return 'title-me'
    }
    if (props.answers.forWhom === ForWhomEnum.NOT_ME) {
      if (props.answers.forWhomPeople > 1) {
        return 'title-not-me-plural'
      }
      return 'title-not-me-singular'
    }
    return 'title'
  }

  function submit() {
    if (isServiceLoading.value.includes(true)) return
    if (!props.onlyHolderPostalCode && samePostalCode.value) {
      fillPostalCodes(policyHolderPostal.value)
    } else if (props.onlyHolderPostalCode) {
      fillPostalCodes(undefined)
    }

    if (forWhom.value !== ForWhomEnum.ME) {
      insureds.value[0].addressDetails.postalCode = policyHolderPostal.value
    }

    return { insureds: insureds.value, policyHolderPostal: policyHolderPostal.value }
  }
</script>
<style lang="scss" scoped>
  .InsuredInfoComponent {
    .text-medium-emphasis {
      padding: 0;
    }

    .grid-inputs {
      display: grid;
      margin-top: $spacing-l;
      grid-template-columns: 5rem 5rem minmax(6rem, 1fr);
      grid-column-gap: $gap-2xl;
    }

    .birth-date-container {
      margin-bottom: $spacing-2xl;
    }

    .birth-date-field {
      margin-top: $spacing-l;
    }

    h3 {
      color: var(--pricing-subtitle-color);
      font-size: var(--pricing-subtitle-size);
    }
  }
</style>
